import { Component, Inject, Injectable, OnInit, ViewChild, ViewChildren } from '@angular/core';
import { UntypedFormControl, Validators, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import * as login from '../../actions/login.actions';
import * as forgotPasscodeActions from '../../actions/forgot-passcode.actions';
import { IForgotPasscodeRequest } from '../../models/forgot-passcode.model';
import { IBusinessLoginRequest } from '../../models/business-login.model';
import { AngularFireAnalytics } from '@angular/fire/compat/analytics';
import { fromStaticDataCountrySelector } from '../../../../static-data/selectors';
import { fromStaticDataCountriesActions } from '../../../../../../src/app/static-data/actions';
import { APP_CONFIG } from '../../../../config/app-config.module';
import { AppConfig } from '../../../../core/config-models/app-config.model';
import { SharedService } from '../../../../shared/shared.service';
import { IntlTelInputDirective } from '../../../../shared/intl-tel-input.directive';
import { IOtpConfig } from '../../models/otp-config.model';
import { map, Observable, take, timer } from 'rxjs';
import * as forgotPasscodeResendOtpActions from '../../actions/forgot-passcode-resend-otp.actions';
import { IForgotPasscodeResentOtpRequest } from '../../models/forgot-passcode-resend-otp.model';
import { IOtpRequest } from '../../../../core/models/otp.model';
import * as forgotPasscodeLoginConfirm from '../../actions/forgot-passcode-login-confirm.actions';
import * as forgotPasscodeLoginConfirmSelector from '../../selectors/forgot-passcode-login-confirm.selectors';
import { Router } from '@angular/router';
import { RouteURL, SessionStorageIds } from '../../../../shared/enums';

@Injectable({
  providedIn: 'root'
})
@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.css']
})
export class SignupComponent implements OnInit {
  @ViewChildren(IntlTelInputDirective) dirs;
  signupForm: any;
  existingMemberCheck: boolean;
  signinForm: UntypedFormGroup;
  fpSubmitMobileForm: UntypedFormGroup;
  fpVerifyOTPForm: UntypedFormGroup;
  fpConfirmPasscodeForm: UntypedFormGroup;
  countries$: any;
  tnc: any[];
  displayLogin: boolean = true;
  displayFPMobileSubmit: boolean = false;
  displayFPOTPVerify: boolean = false;
  displayFPConfirmPasscode: boolean = false;
  otpConfig: Partial<IOtpConfig>;
  otpTimer: number = 30;
  @ViewChild('ngOtpInput') ngOtpInputRef: any;
  countDownTimer$: Observable<number>;
  tick: number;
  enterOtp: boolean;
  userMobileNumber: string;
  countryId: string;
  governmentIdNumber: string;
  hasEmailAddress: boolean;
  subdomain: string;

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private store: Store,
    private sharedService: SharedService,
    private analytics: AngularFireAnalytics,
    @Inject(APP_CONFIG) private appConfig: AppConfig
  ) { }

  ngOnInit(): void {
    this.subdomain = this.sharedService.getSubDomain();
    this.countries$ = this.store.select(fromStaticDataCountrySelector.getStaticDataCountriesResponse);
    this.store.dispatch(fromStaticDataCountriesActions.loadStaticDataCountries({ noLoader: true }));

    this.existingMemberCheck = false;
    this.signupForm = this.fb.group({
      firstname: new UntypedFormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(15)]),
      lastname: new UntypedFormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(15)]),
      mobile: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]+$'), Validators.minLength(5), Validators.maxLength(10)]),
      email: new UntypedFormControl('', [Validators.minLength(2), Validators.maxLength(30)])
    });

    this.signinForm = this.fb.group({
      phone: new UntypedFormControl(undefined, [Validators.required]),
      passcode: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]+$'), Validators.minLength(4), Validators.maxLength(4)])
    });

    this.fpSubmitMobileForm = this.fb.group({
      phone: new UntypedFormControl(undefined, [Validators.required])//,
    });

    this.fpVerifyOTPForm = this.fb.group({
      otp: new UntypedFormControl(undefined, [Validators.required])//,
    });

    sessionStorage.setItem('IsFromToDo', 'false');
    this.sharedService.isFromTODO = false;
    this.otpConfig = {
      length: 6,
      allowNumbersOnly: true,
      inputStyles: {
        'border-left': 'none',
        'border-top': 'none',
        'border-right': 'none',
        'border-bottom': '1px solid',
        outline: 'none',
        'border-bottom-left-radius': '0px',
        'border-bottom-right-radius': '0px'
      }
    };

    //remove any temp session storages
    sessionStorage.removeItem('temp_' + SessionStorageIds.USER_MOBILE);
    sessionStorage.removeItem('temp_' + SessionStorageIds.TOKEN);
    sessionStorage.removeItem('temp_' + SessionStorageIds.REFRESH_TOKEN);
  }

  signIn() {
    const countryData = this.dirs.first.iti.getSelectedCountryData();

    this.analytics.logEvent('user login', { name: 'Sign In!' });
    const req: IBusinessLoginRequest = {
      dialingCode: '+' + countryData.dialCode,
      mobileNumber: this.signinForm.value.phone,
      passcode: this.signinForm.value.passcode
    };
    this.store.dispatch(login.loadLoginRequest({ request: req }));
  }

  get phone() {
    return this.signinForm.get('phone');
  }

  get dialingCode() {
    return this.signinForm.get('dialingCode');
  }

  get passcode() {
    return this.signinForm.get('passcode');
  }

  get mobile() {
    return this.signupForm.get('mobile');
  }

  get firstname() {
    return this.signupForm.get('firstname');
  }

  get lastname() {
    return this.signupForm.get('lastname');
  }

  navigateToLogin(){
    this.displayLogin=true;
    this.displayFPMobileSubmit = false;
    this.displayFPOTPVerify = false;
    this.displayFPConfirmPasscode = false;
  }

  onForgotPasscodeLinkClick(){
    if (this.dirs?.first?.itiInstance) {
      const countryData = this.dirs.first.itiInstance.getSelectedCountryData();
      if (countryData) {
        this.sharedService.forgotPasscodeDialingCode = '+' + countryData.dialCode;
      }
      const phoneValue = this.signinForm.get('phone')?.value;
      if (phoneValue != null && phoneValue !== '') {
        this.sharedService.forgotPasscodeMobileNumber = String(phoneValue);
      }
    }
    this.router.navigateByUrl(RouteURL.AUTH_MOBILE_SUBMIT);
  }

  navigateToForgotPasscodeSubmitMobile(){
    this.displayLogin=false;
    this.displayFPMobileSubmit = true;
    this.displayFPOTPVerify = false;
    this.displayFPConfirmPasscode = false;
  }

  onSubmitMobileClick(){
    this.navigateToForgotPasscodeVerifyOTP();
  }

  navigateToForgotPasscodeVerifyOTP(){
    this.displayLogin=false;
    this.displayFPMobileSubmit = false;
    this.displayFPOTPVerify = true;
    this.displayFPConfirmPasscode = false;
  }

  onVerifyOTPClick(){
    this.navigateToForgotPasscodeConfirmPasscode();
  }

  navigateToForgotPasscodeConfirmPasscode(){
    this.displayLogin=false;
    this.displayFPMobileSubmit = false;
    this.displayFPOTPVerify = false;
    this.displayFPConfirmPasscode = true;
  }

  onConfirmPasscodeClick(){
    this.navigateToLogin();
  }


  resendOtp(val) {
    this.ngOtpInputRef.setValue('');
    this.otpTimer = 30;
    this.enterOtp = true;
    this.store.dispatch(forgotPasscodeResendOtpActions.clearForgotPasscodeResendOtp());
    this.startCountTimer();
    const req: IForgotPasscodeResentOtpRequest = {
      dialingCode: '+27',
      mobileNumber: this.userMobileNumber,
      countryId: this.countryId,
      governmentIdNumber: this.governmentIdNumber,
      uniqueDeviceId: '00000000-0000-0000-0000-000000000000',
      make: 'web',
      model: 'web',
      toMobilePhone: val,
      totalDiskCapacity: '50'
    };
    this.store.dispatch(
      forgotPasscodeResendOtpActions.loadForgotPasscodeResendOtp({
        request: req
      })
    );
  }

  startCountTimer() {
    this.countDownTimer$ = timer(0, this.tick).pipe(
      take(this.otpTimer),
      map(() => --this.otpTimer)
    );
  }

  onOtpChange(otpDigits: string) {
    const req: IOtpRequest = {
      dialingCode: '+27',
      mobileNumber: this.userMobileNumber,
      otp: otpDigits
    };

    if (otpDigits.length === 6 && this.enterOtp) {
      this.enterOtp = false;
      this.navigateToForgotPasscodeConfirmPasscode();
    } else if (otpDigits.length < 6) {
      this.enterOtp = true;
    }
  }



  forgot() {
    this.analytics.logEvent('user login', { name: 'forgot' });
    this.store.dispatch(forgotPasscodeActions.clearForgotPasswordsUserNeedsHelpFailure());
    const req: IForgotPasscodeRequest = {
      dialingCode: this.signinForm.value.phone.dialCode,
      mobileNumber: this.signinForm.value.mobileSignin,
      uniqueDeviceId: '00000000-0000-0000-0000-000000000000',
      make: 'web',
      model: 'web',
      totalDiskCapacity: '50'
    };

    this.store.dispatch(
      forgotPasscodeActions.loadForgotPasswordsUserNeedsHelpRequest({
        request: req
      })
    );
  }

  navSubmitMobileBackBtn() {
    this.navigateToLogin();
  }

  navVerifyOTPBackBtn() {
    this.navigateToForgotPasscodeSubmitMobile();
  }

  navConfirmPasscodeBackBtn() {
    this.navigateToForgotPasscodeVerifyOTP();
  }
}
